import {
  WSElement,
  WSElementProps,
  WSFlexGrid,
  WSForm,
  WSSelect,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { IAddress } from "@wingspanhq/users";
import React from "react";
import { useFormContext } from "react-hook-form";
import {
  COUNTRY_OPTIONS,
  US_STATES_OPTIONS_SHORT_NAME
} from "../../../constants/user";

export type AddressValues = {
  country: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
};

export const addressToValues = (address?: IAddress): AddressValues => ({
  country: address?.country || "US",
  addressLine1: address?.addressLine1 || "",
  addressLine2: address?.addressLine2 || "",
  city: address?.city || "",
  state: address?.state || "",
  zipCode: address?.postalCode || ""
});

export const addressValuesToRequest = (values: AddressValues): IAddress => ({
  country: values.country,
  addressLine1: values.addressLine1,
  addressLine2: values.addressLine2,
  city: values.city,
  state: values.state,
  postalCode: values.zipCode
});

type Props = {
  name: string;
  disableCountry?: boolean;
} & WSElementProps;

export const AddressField: React.FC<Props> = ({
  name,
  disableCountry,
  ...elementProps
}) => {
  const { watch } = useFormContext();
  const addressValues = watch(name);

  return (
    <WSElement {...elementProps}>
      <WSForm.Field
        name={name + ".country"}
        label="Country"
        component={WSSelect}
        componentProps={{
          options: disableCountry
            ? COUNTRY_OPTIONS.filter(country => country.value === "US")
            : COUNTRY_OPTIONS,
          searchable: !disableCountry,
          searchPlaceholder: "Search country..."
        }}
        mb="M"
      />

      <WSForm.Field
        name={name + ".addressLine1"}
        label="Street address"
        component={WSTextInput}
        mb="S"
      />

      <WSForm.Field
        name={name + ".addressLine2"}
        component={WSTextInput}
        mb="M"
      />

      <WSElement>
        <WSFlexGrid container spacing="S">
          <WSFlexGrid item mobile="12" tablet="12" desktop="6">
            <WSForm.Field
              name={name + ".city"}
              component={WSTextInput}
              label="City"
            />
          </WSFlexGrid>
          {addressValues.country === "US" ? (
            <>
              <WSFlexGrid item mobile="12" tablet="6" desktop="3">
                <WSForm.Field
                  name={name + ".state"}
                  component={WSSelect}
                  componentProps={{
                    options: US_STATES_OPTIONS_SHORT_NAME,
                    searchable: true
                  }}
                  label="State"
                />
              </WSFlexGrid>
              <WSFlexGrid item mobile="12" tablet="6" desktop="3">
                <WSForm.Field
                  name={name + ".zipCode"}
                  component={WSTextInput}
                  componentProps={{
                    formatOptions: {
                      numericOnly: true,
                      blocks: [5]
                    }
                  }}
                  label="Zip code"
                />
              </WSFlexGrid>
            </>
          ) : (
            <WSFlexGrid item mobile="12" tablet="6" desktop="6">
              <WSForm.Field
                name={name + ".state"}
                component={WSTextInput}
                label="Region/state (optional)"
              />
            </WSFlexGrid>
          )}
        </WSFlexGrid>
        {addressValues.country !== "US" && (
          <WSForm.Field
            mt="S"
            name={name + ".zipCode"}
            component={WSTextInput}
            label="Postal code"
          />
        )}
      </WSElement>
    </WSElement>
  );
};
